/**
 * @description API service for time clock dashboard operations
 *
 * Key features:
 * - Time entry list with filtering and pagination
 * - Manual entry creation
 * - Entry editing and deletion
 * - CSV export
 *
 * @dependencies
 * - apiClient: Configured axios instance with auth headers
 *
 * @notes
 * - All endpoints require Wristband authentication
 * - company_id derived from auth context on backend
 */

import apiClient from './apiClient';

/**
 * Extract just the date portion (YYYY-MM-DD) from a timestamp
 * Handles both ISO strings (2026-01-31T00:00:00.000Z) and date-only strings
 */
const normalizeDate = (dateValue) => {
  if (!dateValue) return null;
  // If it's already just a date (YYYY-MM-DD), return as-is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  // Extract date portion from ISO timestamp
  const str = String(dateValue);
  return str.substring(0, 10);
};

/**
 * Transform snake_case PTO request to camelCase for frontend use
 */
const formatPtoRequest = (request) => ({
  id: request.id,
  companyId: request.company_id,
  employeeId: request.employee_id,
  employeeName: request.employee_name,
  employeeEmail: request.employee_email,
  leaveType: request.leave_type,
  startDate: normalizeDate(request.start_date),
  endDate: normalizeDate(request.end_date),
  startTime: request.start_time,
  endTime: request.end_time,
  isPartialDay: request.is_partial_day,
  hoursRequested: request.hours_requested,
  reason: request.reason,
  status: request.status,
  reviewedBy: request.reviewed_by,
  reviewerName: request.reviewer_name,
  reviewedAt: request.reviewed_at,
  reviewNotes: request.review_notes,
  createdAt: request.created_at,
  updatedAt: request.updated_at,
});

const timeClockService = {
  /**
   * Get time entries with filters
   */
  getTimeEntries: async (params = {}) => {
    const response = await apiClient.get('/time-entries', { params });
    return response.data;
  },

  /**
   * Get a single time entry
   */
  getTimeEntry: async (id) => {
    const response = await apiClient.get(`/time-entries/${id}`);
    return response.data;
  },

  /**
   * Create a manual time entry
   */
  createManualEntry: async (data) => {
    const response = await apiClient.post('/time-entries/manual', data);
    return response.data;
  },

  /**
   * Edit a time entry
   */
  editTimeEntry: async (id, data) => {
    const response = await apiClient.put(`/time-entries/${id}`, data);
    return response.data;
  },

  /**
   * Delete (soft) a time entry
   */
  deleteTimeEntry: async (id) => {
    const response = await apiClient.delete(`/time-entries/${id}`);
    return response.data;
  },

  /**
   * Export time entries as CSV (returns blob)
   */
  exportTimeEntries: async (params = {}) => {
    const response = await apiClient.get('/time-entries/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Set/reset an employee's clock PIN
   */
  setEmployeePin: async (employeeId, pin) => {
    const response = await apiClient.put(`/employees/${employeeId}/clock-pin`, { pin });
    return response.data;
  },

  /**
   * Get breaks for a time entry
   */
  getBreaks: async (entryId) => {
    const response = await apiClient.get(`/time-entries/${entryId}/breaks`);
    return response.data;
  },

  /**
   * Edit a break
   */
  editBreak: async (entryId, breakId, data) => {
    const response = await apiClient.put(`/time-entries/${entryId}/breaks/${breakId}`, data);
    return response.data;
  },

  /**
   * Delete a break
   */
  deleteBreak: async (entryId, breakId, editReason) => {
    const response = await apiClient.delete(`/time-entries/${entryId}/breaks/${breakId}`, {
      data: { editReason },
    });
    return response.data;
  },

  /**
   * Add a manual break to a time entry
   */
  addBreak: async (entryId, data) => {
    const response = await apiClient.post(`/time-entries/${entryId}/breaks`, data);
    return response.data;
  },

  // ========== PTO Endpoints ==========

  /**
   * Get PTO requests with filters
   */
  getPtoRequests: async (params = {}) => {
    const response = await apiClient.get('/pto/requests', { params });
    return {
      ...response.data,
      requests: (response.data.requests || []).map(formatPtoRequest),
    };
  },

  /**
   * Get a single PTO request
   */
  getPtoRequest: async (id) => {
    const response = await apiClient.get(`/pto/requests/${id}`);
    return {
      ...response.data,
      request: response.data.request ? formatPtoRequest(response.data.request) : null,
    };
  },

  /**
   * Approve a PTO request
   */
  approvePtoRequest: async (id, notes = null) => {
    const response = await apiClient.put(`/pto/requests/${id}/approve`, { notes });
    return {
      ...response.data,
      request: response.data.request ? formatPtoRequest(response.data.request) : null,
    };
  },

  /**
   * Deny a PTO request
   */
  denyPtoRequest: async (id, notes) => {
    const response = await apiClient.put(`/pto/requests/${id}/deny`, { notes });
    return {
      ...response.data,
      request: response.data.request ? formatPtoRequest(response.data.request) : null,
    };
  },

  /**
   * Get all employee balances
   */
  getAllPtoBalances: async (params = {}) => {
    const response = await apiClient.get('/pto/balances', { params });
    return response.data;
  },

  /**
   * Adjust an employee's PTO balance
   */
  adjustPtoBalance: async (employeeId, balanceType, adjustment, year = null) => {
    const response = await apiClient.put(`/pto/balances/${employeeId}/adjust`, {
      balanceType,
      ...adjustment,
      year,
    });
    return response.data;
  },

  /**
   * Approved/taken PTO for calendar overlay (manager schedule)
   */
  getPtoCalendar: async (startDate, endDate) => {
    const response = await apiClient.get('/pto/calendar', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  /**
   * Create a new PTO request
   */
  createPtoRequest: async (data) => {
    const response = await apiClient.post('/pto/requests', data);
    return {
      ...response.data,
      request: response.data.request ? formatPtoRequest(response.data.request) : null,
    };
  },

  /**
   * Cancel a PTO request (employee cancelling their own pending or approved request)
   */
  cancelPtoRequest: async (id, reason = null) => {
    const response = await apiClient.put(`/pto/requests/${id}/cancel`, { reason });
    return {
      ...response.data,
      request: response.data.request ? formatPtoRequest(response.data.request) : null,
    };
  },
};

export default timeClockService;
